// The Edit team sheet, reachable from anywhere a league is on screen (SCENARIOS L2).
//
// ONE SHEET, SEVERAL DOORS. It opens from the team's own header on My Team
// (`LineupScreen`), from "Edit my team" in the league switcher, and from my
// seat on a league's pre-draft page. Each door asks this provider rather than
// mounting a sheet of its own, so there is one sheet to keep right and the
// switcher — which lives in the shell, beside no screen — can open it too.
//
// A door is offered only where there is a team of mine on screen
// (`useMyTeamHere`); a league I watch without a seat has nothing to edit.
//
// THE SHEET BELONGS TO THE PAGE IT WAS OPENED ON. It remembers the team and the
// path it was opened for, and closes — for good, not until the next time that
// team is on screen — the moment either changes. A phone's Back used to leave
// the page without touching the sheet, and until the review of 2026-09-25 the
// sheet stayed "open" underneath: it came back by itself in the next league
// with a team of mine, or swapped to another league's team mid-edit and
// dropped what had been typed. Back closes the sheet first now
// (`kit/backCloses.tsx`); a link out of it still leaves the page, and this
// still closes it then.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { EditTeamSheet } from '../screens/shared/EditTeamSheet';
import { useMyTeamHere } from '../data/team';

interface EditTeamValue {
  /** Open the sheet on my team in the league on screen. Nothing happens where there is none. */
  open: () => void;
}

const EditTeamContext = createContext<EditTeamValue | null>(null);

/** Which team, on which page, the sheet was opened for. */
interface OpenedOn {
  team: string;
  path: string;
}

export function EditTeamProvider({ children }: { children: ReactNode }) {
  const here = useMyTeamHere();
  const { pathname } = useLocation();
  const team = here === null ? null : `${here.leagueId}|${here.team.id}`;
  const [openedOn, setOpenedOn] = useState<OpenedOn | null>(null);

  // The doors call `open` from a click; the team and the path they mean are
  // the ones on screen at that moment.
  const current = useRef({ team, path: pathname });
  current.current = { team, path: pathname };

  const open = useCallback(() => {
    const { team: now, path } = current.current;
    if (now !== null) setOpenedOn({ team: now, path });
  }, []);
  const close = useCallback(() => setOpenedOn(null), []);

  // Left behind: another page, another team, or none. Forgotten rather than
  // hidden, so coming back is not a reason to open again.
  const stale = openedOn !== null && (openedOn.team !== team || openedOn.path !== pathname);
  useEffect(() => {
    if (stale) setOpenedOn(null);
  }, [stale]);

  const value = useMemo(() => ({ open }), [open]);
  return (
    <EditTeamContext.Provider value={value}>
      {children}
      <EditTeamSheet opened={openedOn !== null && !stale} onClose={close} />
    </EditTeamContext.Provider>
  );
}

export function useEditTeam(): EditTeamValue {
  const ctx = useContext(EditTeamContext);
  if (!ctx) throw new Error('useEditTeam must be used inside EditTeamProvider');
  return ctx;
}
